import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/lib/auth-context';

export type BillingSnapshot = {
  scope: 'platform' | 'tenant';
  tenant: null | {
    name?: string;
    max_users: number | null;
    max_audio_files: number | null;
    max_image_files: number | null;
    max_storage_bytes: number | null;
  };
  usage: null | {
    users: number;
    audioFiles: number;
    imageFiles: number;
    storageBytes: number;
  };
};

export function TenantQuotaBanner() {
  const { user } = useAuth();
  const [snapshot, setSnapshot] = useState<BillingSnapshot | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user || user.role === 'super_admin' || user.tenantAccessMode === 'expired_read_only') return;
    let current = true;
    apiClient.get<BillingSnapshot>('/participants/me/billing')
      .then((response) => { if (current) setSnapshot(response.data); })
      .catch(() => undefined);
    return () => { current = false; };
  }, [user]);

  const isAdmin = ['admin', 'project_manager'].includes(user?.role || '');
  const message = useMemo(() => (snapshot ? quotaNotice(snapshot, isAdmin) : null), [snapshot, isAdmin]);

  if (!message || dismissed || user?.tenantAccessMode === 'expired_read_only') return null;
  return (
    <div role="alert" className="flex shrink-0 items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-950">
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1">
        {message}
      </span>
      <button type="button" onClick={() => setDismissed(true)} className="grid h-6 w-6 shrink-0 place-items-center rounded-full hover:bg-amber-100" aria-label="Dismiss quota notice"><X className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

/**
 * The notice for every limit the organization has reached, with numbers, or null.
 * Blank (null) is unlimited; 0 means none allowed, so it counts as reached straight away.
 */
export function quotaNotice(snapshot: BillingSnapshot, isAdmin: boolean): string | null {
  if (!snapshot.tenant || !snapshot.usage) return null;
  const limits: Array<{ label: string; used: number; limit: number | null; bytes?: boolean }> = [
    { label: 'user limit', used: snapshot.usage.users, limit: snapshot.tenant.max_users },
    { label: 'audio file limit', used: snapshot.usage.audioFiles, limit: snapshot.tenant.max_audio_files },
    { label: 'image and motion file limit', used: snapshot.usage.imageFiles, limit: snapshot.tenant.max_image_files },
    { label: 'storage limit', used: snapshot.usage.storageBytes, limit: snapshot.tenant.max_storage_bytes, bytes: true },
  ];
  const reached = limits.filter((item) => item.limit !== null && item.limit !== undefined && Number(item.used) >= Number(item.limit));
  if (!reached.length) return null;
  const amount = (value: number, bytes?: boolean) => (bytes ? formatBytes(value) : Number(value).toLocaleString('en-US'));
  const parts = reached.map((item) =>
    Number(item.limit) === 0
      ? `${item.label} (set to 0, so none are allowed)`
      : `${item.label} (${amount(item.used, item.bytes)} of ${amount(Number(item.limit), item.bytes)})`,
  );
  const list = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0];
  const organization = snapshot.tenant.name || 'Your organization';
  return `${organization} has reached its ${list}. ${isAdmin ? 'Contact Dexter to raise it.' : 'Contact your organization administrator.'}`;
}
